import { FC } from 'react';
import { useTranslation } from 'next-i18next';

import { AssetTypeValue } from '@/entities/assets/components/AssetTypeValue';
import { useFormFields } from '@/entities/assets/hooks/useFormFields';
import { Address } from '@/shared/components';
import { renderDtoIdFieldView } from '@/shared/components/forms/fields/renderDtoIdFieldView';
import { SelectFromListFieldView } from '@/shared/components/forms/fields/SelectFromListFieldView';
import { Address as AddressType } from '@/shared/lib/forms/form-wrapper.types';
import { SJDatabase } from '@/shared/lib/sj-orm';
import { AssetDto } from '@/shared/lib/sj-orm/models/asset.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { dates } from '@/shared/utils/dates';

const EMPTY_TEXT = '-';

type AssetFieldValueProps = {
  asset: AssetDto;
  step: number;
  fieldKey: string;
  sjDb: SJDatabase;
  isPdf?: boolean;
};

export const AssetFieldValue: FC<AssetFieldValueProps> = ({
  fieldKey,
  step,
  asset,
  sjDb,
  isPdf,
}) => {
  const { formData } = useFormFields({ step, asset });
  const { t } = useTranslation('common');
  const fieldDef = formData[fieldKey];
  const value = asset?.[fieldKey as keyof typeof asset];
  const type = fieldDef?.type;

  if (fieldKey === 'type' && !isPdf) {
    return <AssetTypeValue assetInfo={{ category: asset!.category, type: asset!.type }} />;
  }

  switch (type) {
    case QuestionType.PICK_ONE_OF_DTOS:
    case QuestionType.PICK_ARRAY_OF_DTO:
      return renderDtoIdFieldView({ ids: value as string[], emptyText: EMPTY_TEXT, sjDb, isPdf });

    case QuestionType.CHECKBOX:
      return value ? t('yes') : t('no');

    case QuestionType.DATE_TIME:
      return value ? dates.formatDate(value as string) : EMPTY_TEXT;

    case QuestionType.PHONE_NUMBER: {
      if (typeof value === 'string') {
        return value;
      }
      const number = value as { Dropdown: string; Input: string } | undefined;
      return number?.Dropdown && number.Input ? `${number.Dropdown}${number.Input}` : EMPTY_TEXT;
    }

    case QuestionType.SELECT_FROM_LIST:
      return (
        <SelectFromListFieldView
          fieldKey={fieldKey}
          value={value as string | string[]}
          formFieldSelectItems={fieldDef.items}
          emptyText={EMPTY_TEXT}
          multiple={fieldDef?.mode === 'multiple'}
          isPdf={isPdf}
        />
      );

    case QuestionType.ADDRESS: {
      return <Address value={value as AddressType} isPdf={isPdf} />;
    }

    default:
      return value ? String(value) : EMPTY_TEXT;
  }
};
